import useFetch from 'use-http/dist/esm/index';
import { getSigninEndPoint } from '../../utils/endpoint';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useLocalStorage from '../../hooks/useLocaleStorage';
import useAppActions from '../../actions/useAppAction';
import { func, bool } from 'prop-types';
import { DBNAME, errorPrivateNavigationFirefox } from '../../utils/constants';
import NavigatorNotCompatible from '../Technique/TestTechnique/Errors/NavigatorNotCompatible';

const SigninApp = ({ setUsedSignin, ...routerProps }) => {
	const [localToken, setLocalToken] = useLocalStorage('localToken');
	const [storageError, setErrorStorage] = useState(null);
	const [, setShowSurvey] = useLocalStorage('survey', void 0, 'sessionStorage');
	const { setTestTechnique } = useAppActions();

	const {
		match: {
			params: { token, step },
		},
	} = routerProps;

	const { post, response } = useFetch({ suspense: true });

	const clearDbIfExists = async () => {
		const dbModule = (await import('../../utils/technical-tests/disk-storage')).default;
		const DiskStorage = await dbModule({ dataBaseName: DBNAME, mode: 'readwrite' });
		const count = await DiskStorage.api.count();

		if (count) {
			DiskStorage.api.clear();
		}
	};

	/**
	 * If db exist we need to clear all databases from iCims
	 * @return {Promise<void>}
	 * method databases is not available on firefox
	 */
	const checkDatabase = useCallback(async () => {
		if (indexedDB) {
			if (indexedDB.databases?.()) {
				const dbs = await indexedDB.databases();
				const hasDbs = dbs.some(({ name }) => name === DBNAME);

				if (hasDbs) {
					await clearDbIfExists();
				}
			} else {
				await clearDbIfExists();
			}
		}
	}, []);

	/**
	 * send Signin and reset redux persist and delete localstorage, and indexedDb (big reset)
	 * @type {Function}
	 */
	const sendSignin = useCallback(async () => {
		try {
			setTestTechnique(false);
			await post(getSigninEndPoint, {
				token,
			});
			if (response.ok) {
				localStorage.clear();
				sessionStorage.clear();
				await checkDatabase();
				setLocalToken(token);
				return setUsedSignin(true);
			}
		} catch (err) {
			if (err.message === errorPrivateNavigationFirefox) {
				setErrorStorage(errorPrivateNavigationFirefox);
			}
		}
	}, [setTestTechnique, post, token, response, checkDatabase, setLocalToken, setUsedSignin]);

	const needSignin = useMemo(
		() => step === 'start' || !localToken || localToken !== token,
		[step, localToken, token]
	);

	useEffect(() => {
		(async function signFunc() {
			if (needSignin) {
				await sendSignin();
				return null;
			}
			if (step === 'survey') {
				setShowSurvey(true);
			}

			setUsedSignin(true);
			return setLocalToken(token);
		})();
	}, [sendSignin, needSignin, setUsedSignin, setLocalToken, token, step, setShowSurvey]);

	if (storageError) {
		return <NavigatorNotCompatible showButtonContactSupport={false} />;
	}

	return null;
};

SigninApp.propTypes = {
	setUsedSignin: func,
	usedSignin: bool,
};

export default SigninApp;
